import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useUserProfile } from '../contexts/UserProfileContext'

/** Profile key the stats layout lives under — this UI's own. */
export const STATS_LAYOUT_KEY = 'ruiStatsLayout'

/**
 * Where a stats panel shows a metric: a tile under the chart, a row in the
 * table under the tiles, or only in the all-stats list.
 */
export type StatDisplay = 'card' | 'table' | 'hidden'

/** Per panel variant, the metrics the user moved away from where they start. */
type StatsLayout = Record<string, Record<string, StatDisplay>>

function without<T>(record: Record<string, T>, key: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([k]) => k !== key))
}

/**
 * Where each metric of a stats panel goes, as the user arranged it, keyed by
 * the field the API reports it as. Kept in the user profile per variant rather
 * than per object, so a layout set on one line item holds on every line item,
 * and follows the user between browsers. Only the metrics moved from their
 * default are stored; a metric with no default is hidden until placed.
 */
export function useStatsLayout(variant: string, defaults: Record<string, StatDisplay>) {
  const { t } = useTranslation()
  const { read, save } = useUserProfile()
  const saved = read<unknown>(STATS_LAYOUT_KEY, {})
  const layout: StatsLayout = saved && typeof saved === 'object' ? (saved as StatsLayout) : {}

  // Moved at once, so the menu answers the click rather than the round trip.
  const write = (next: StatsLayout) =>
    save({ [STATS_LAYOUT_KEY]: next }).catch(() => toast.error(t('stats.layoutSaveFailed')))

  const overrides = layout[variant] ?? {}
  const displayOf = (key: string): StatDisplay => overrides[key] ?? defaults[key] ?? 'hidden'

  const store = (nextOverrides: Record<string, StatDisplay>) => {
    const others = without(layout, variant)
    write(
      Object.keys(nextOverrides).length > 0 ? { ...others, [variant]: nextOverrides } : others
    )
  }

  const setDisplay = (key: string, display: StatDisplay) => {
    const rest = without(overrides, key)
    store(display === (defaults[key] ?? 'hidden') ? rest : { ...rest, [key]: display })
  }

  return {
    displayOf,
    setDisplay,
    isCustomized: Object.keys(overrides).length > 0,
    reset: () => store({}),
  }
}
